import { useEnvContext, useWebStoreContext } from "contexts";
import { FeaturesFlags } from "helpers";
import { useAppDispatch, useAppSelector, usePopupsState } from "hooks";
import { useLinks } from "hooks/useLinks";
import useNewDay from "hooks/useNewDay";
import { PopUpVariant, WsDialogs } from "interfaces";
import { DripOfferResponse, Tracker } from "interfaces/drip-offer";
import { FeatureType } from "interfaces/feature-tracker";
import { getAvailablePrizes, getFilteredTracker } from "pages/DripOffer/hooks";
import { useEffect, useRef, useState } from "react";
import {
	featureTrackerAPI,
	useGetFeatureTrackersQuery,
	useSuppressResponseMutation
} from "redux/apis";
import { onOpenPopUp, selectDipState } from "redux/slices";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";

export const useDripOffer = ({
	suppressPopups
}: {
	suppressPopups?: boolean;
}): void => {
	const [flag, setFlag] = useState<{
		dripOffer: boolean;
		hasSeenMainPage: boolean;
	}>({
		dripOffer: false,
		hasSeenMainPage: false
	});

	const { newDay } = useNewDay();
	const links = useLinks();
	const dispatch = useAppDispatch();
	const { isFeatureFlagEnabled } = useEnvContext();
	const { hasSeenRequiredPageTutorial, isAnyPopupOpen } = usePopupsState();
	const { variant: dialogVariant } = useDialogModalRedux();
	const { userTracker, handleErrorResponse } = useWebStoreContext();
	const [showEventPopup, setShowEventPopup] = useState<PopUpVariant>();
	const { opened: dipOpened, triggerCached } = useAppSelector(selectDipState);
	const suppressedTrackerKeys = useRef(new Set<string>());

	const [SuppressResponse] = useSuppressResponseMutation();

	const {
		data: dripOfferData,
		refetch: refetchDripOffer,
		isSuccess: isSuccessDripOffer
	}: {
		data?: DripOfferResponse;
		refetch: () => void;
		isSuccess: boolean;
	} = useGetFeatureTrackersQuery(
		{
			FeatureType: FeatureType.DRIP_OFFER,
			isAuthenticated: !!userTracker
		},
		{
			skip: !isFeatureFlagEnabled(FeaturesFlags.DRIP_OFFER) || !userTracker
		}
	);

	useEffect(() => {
		if (!dipOpened && !isAnyPopupOpen && showEventPopup) {
			dispatch(onOpenPopUp(showEventPopup));
			setShowEventPopup(undefined);
		}
	}, [dipOpened, isAnyPopupOpen, showEventPopup]);

	useEffect(() => {
		if (window.location.pathname === "/special-offers") {
			//replace 2 flags for one
			setFlag((flag) => ({ ...flag, hasSeenMainPage: true }));
		}
	}, [window.location.pathname]);

	useEffect(() => {
		if (newDay) {
			refetchDripOffer();
		}
	}, [newDay]);

	useEffect(() => {
		if (suppressPopups && window.location.pathname === links.shop.path) {
			return;
		}
		if (dripOfferData?.trackers) {
			const filteredTracker = getFilteredTracker(dripOfferData.trackers);
			if (
				triggerCached !== "sessionStart" &&
				isSuccessDripOffer &&
				filteredTracker &&
				!filteredTracker.isFinished &&
				window.location.pathname !== "/" &&
				window.location.pathname !== "/special-offers" &&
				!flag.dripOffer &&
				!flag.hasSeenMainPage &&
				!dipOpened &&
				!isAnyPopupOpen &&
				hasSeenRequiredPageTutorial
			) {
				setFlag((flag) => ({ ...flag, dripOffer: true }));
				dispatch(onOpenPopUp("drip-offer"));
			}
			const finishedRewardsCollection = (tracker?: Tracker) => {
				if (
					tracker &&
					getAvailablePrizes(tracker.nodes) === 0 &&
					dialogVariant !== WsDialogs.XSOLLA_PURCHASE
				) {
					const trackerKey = `${tracker.userId}:${tracker.eventId}`;
					if (suppressedTrackerKeys.current.has(trackerKey)) {
						return;
					}
					suppressedTrackerKeys.current.add(trackerKey);
					SuppressResponse({ eventId: tracker.eventId })
						.unwrap()
						.then(() => {
							if (tracker.offerPurchaseDate) {
								setShowEventPopup("drip-offer-missed-event");
							}
						})
						.catch((error) => {
							suppressedTrackerKeys.current.delete(trackerKey);
							handleErrorResponse({ response: error });
						})
						.finally(() => {
							dispatch(
								featureTrackerAPI.util.invalidateTags([
									{
										type: "featureTrackerTag",
										id: FeatureType.DRIP_OFFER
									}
								])
							);
						});
				}
			};
			const finishedTracker: Tracker | undefined =
				dripOfferData?.trackers?.find(
					(t) => t.isFinished || t.isFinished === undefined
				);
			if (
				dripOfferData?.trackers &&
				finishedTracker &&
				triggerCached !== "sessionStart" &&
				window.location.pathname !== "/"
			) {
				finishedRewardsCollection(finishedTracker);
			}
		}
	}, [
		dripOfferData,
		isSuccessDripOffer,
		window.location.pathname,
		suppressPopups,
		hasSeenRequiredPageTutorial,
		isAnyPopupOpen
	]);
};
